import { ldb, state, router } from '@codesuma/baseline'
import calendar from './calendar'
import customOccasions from './custom-occasions'
import tasks, { ITask } from './tasks'
import events from './events'
import settings from './settings'
import { Reminder } from './occasion-repeat'
import { IReminderAt, dateReminder, effectiveReminder, eventReminders, instantReminder, occasionReminders } from './occurrences'
import { dayLine } from '../helpers/day-line'

// Delivers the reminders the wizard and the task options collect.
//
// The operating system does the waiting, not the app: every reminder is worked
// out as a Gregorian instant (see services/occurrences.ts) and handed to
// LocalNotifications as a one-off. Nothing repeats at the OS level, because an
// anniversary kept in Jalaali or hijri months has no Gregorian repeat rule —
// so the app recomputes the coming ones and re-registers them on every launch
// and after every change.
//
// The plugin is reached through window.Capacitor rather than imported, the way
// the theme service reaches SystemBars: the native plugin is a dependency of
// the shell, not of the app bundle, which stays on baseline alone.

// Two per occasion: this year's and next year's. Enough that the list survives
// a phone left unopened for a year, without crowding the cap below.
const PER_OCCASION = 2
// An occasion is an anniversary, so two of them reach a year out. An event
// repeats on the Gregorian calendar and may repeat daily, where the same two
// would run out the day after tomorrow. A fortnight of a daily event — a
// season of a weekly one — is the stretch of silence worth covering, since the
// schedule is rebuilt on every day rollover the app is awake for.
const PER_EVENT = 14
// iOS keeps at most 64 pending local notifications per app and silently drops
// the rest, so the soonest ones win.
const MAX_PENDING = 60

// What the notification says above the date.
const LEAD_PHRASE: Record<Reminder, string> = {
    none: '',
    week: 'یک هفته دیگر',
    day: 'فردا',
    hour: 'امروز',
}

// Web only: the last time the app looked, so a reminder that came due while it
// was closed can still be shown once on the next visit.
const SEEN_KEY = 'reminders-seen'

interface IPlanned extends IReminderAt {
    title: string
    reminder: Reminder
}

const capacitor = () => (window as any).Capacitor
const plugin = () => capacitor()?.Plugins?.LocalNotifications
const isNative = () => !!capacitor()?.isNativePlatform?.()

// Everything due at or after `from`, soonest first and capped.
function plan(from: Date): IPlanned[] {
    const out: IPlanned[] = []

    for (const o of customOccasions.all()) {
        for (const due of occasionReminders(o, from, PER_OCCASION)) {
            out.push({ ...due, title: o.title, reminder: o.reminder })
        }
    }
    for (const t of tasks.all()) {
        // A task already ticked off has nothing left to remind anyone about.
        if (t.done) continue
        const reminder = effectiveReminder(t.reminder, t.time)
        // A task given an hour counts its lead back from that hour, the way an
        // event does. One left on the day as a whole keeps the day's own rule.
        const coming = t.time
            ? instantReminder(taskAt(t), reminder, from)
            : dateReminder(t.gy, t.gm, t.gd, reminder, from)
        for (const due of coming) {
            out.push({ ...due, title: t.title, reminder })
        }
    }

    // An event carries its own clock, so its lead counts back from the start
    // rather than from the day — see instantReminder.
    for (const e of events.all()) {
        if (e.kind === 'occasion') continue // already planned with its calendar above
        for (const due of eventReminders(e, from, PER_EVENT)) {
            out.push({ ...due, title: e.title, reminder: e.reminder })
        }
    }

    return out.sort((a, b) => a.at.getTime() - b.at.getTime()).slice(0, MAX_PENDING)
}

// The instant a task with an hour falls due, in local time.
function taskAt(t: ITask): Date {
    const [h, m] = String(t.time).split(':').map(Number)
    return new Date(t.gy, t.gm - 1, t.gd, h || 0, m || 0)
}

// The day the reminder is about, written the way the app writes dates, plus the
// query that opens it.
function dayOf(p: IPlanned) {
    const { occurrence } = p
    const cell = calendar.dayAt(occurrence.getFullYear(), occurrence.getMonth() + 1, occurrence.getDate(), 0)
    return {
        text: dayLine(cell, settings.get().system),
        query: `${cell.gy}-${cell.gm}-${cell.gd}`,
    }
}

async function scheduleNative(coming: IPlanned[]) {
    const notifications = plugin()
    if (!notifications) return

    // Asked for only once there is something to deliver, so a user who never
    // sets a reminder is never prompted.
    let allowed = (await notifications.checkPermissions())?.display
    if (allowed !== 'granted') {
        if (!coming.length) return
        allowed = (await notifications.requestPermissions())?.display
        if (allowed !== 'granted') return
    }

    // The whole schedule is rebuilt rather than diffed: the list is small, and
    // a moved hijri date or an edited occasion can change any entry in it.
    const pending = (await notifications.getPending())?.notifications || []
    if (pending.length) await notifications.cancel({ notifications: pending })
    if (!coming.length) return

    await notifications.schedule({
        notifications: coming.map((p, i) => {
            const day = dayOf(p)
            return {
                // Rebuilt every time, so a plain sequence is enough — and it
                // keeps the ids inside the 32-bit range the platforms want.
                id: i + 1,
                title: p.title,
                body: `${LEAD_PHRASE[p.reminder]} — ${day.text}`,
                schedule: { at: p.at, allowWhileIdle: true },
                // Deliberately the name of a file that is in neither bundle.
                // iOS plays nothing at all unless this property is set, and a
                // name it cannot resolve is what makes it fall back to the
                // phone's own notification sound — which is the one to use,
                // rather than a sound of ours. Android never looks the file up
                // either: an unresolved name leaves the builder on DEFAULT_ALL,
                // and from Android 8 the channel owns the sound regardless. So
                // never add a default.wav to a bundle; that would turn this
                // into a custom sound on iOS.
                //
                // The fallback, if a device ever shows this silent:
                // emrouz_calendar_chime.wav is bundled on both sides already,
                // and naming it here is the whole switch.
                sound: 'default.wav',
                // Read back when the notification is tapped; see init below.
                extra: { d: day.query },
            }
        }),
    })
}

// The web has no way to wake a closed tab on a timer, so it does what it can:
// anything falling due while the app is open fires from a timer, and anything
// missed since the last visit is shown once on arrival.
let timers: number[] = []

function scheduleWeb(coming: IPlanned[], now: Date) {
    for (const t of timers) clearTimeout(t)
    timers = []
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return

    for (const p of coming) {
        const wait = p.at.getTime() - now.getTime()
        // setTimeout tops out around 24 days; anything further off will be
        // picked up on a later visit anyway.
        if (wait < 0 || wait > 2 ** 31 - 1) continue
        timers.push(window.setTimeout(() => show(p), wait))
    }
}

function show(p: IPlanned) {
    // Moves the watermark as each one goes out, so a tab left open for days
    // does not have them shown a second time on the next visit.
    ldb.set(SEEN_KEY, String(Date.now()))
    const day = dayOf(p)
    const note = new Notification(p.title, { body: `${LEAD_PHRASE[p.reminder]} — ${day.text}`, tag: day.query })
    note.onclick = () => {
        window.focus()
        router.goto(`/day?d=${day.query}`)
    }
}

// Web: reminders that came due while the app was closed, shown once each.
function catchUp(now: Date) {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
    const seen = Number(ldb.get(SEEN_KEY)) || now.getTime()
    for (const p of plan(new Date(seen))) {
        if (p.at.getTime() <= now.getTime()) show(p)
    }
}

function rebuild() {
    const now = new Date()
    // Switched off in the settings sheet: nothing is worked out, and handing
    // the empty list on is what takes the pending ones back off the phone.
    const coming = settings.get().remindersOff ? [] : plan(now)
    if (isNative()) {
        scheduleNative(coming)
    } else {
        scheduleWeb(coming, now)
        ldb.set(SEEN_KEY, String(now.getTime()))
    }
}

// Changes arrive in bursts — ticking a task writes the store, which broadcasts,
// which redraws — so the schedule is rebuilt once the burst has settled rather
// than once per event. The last call always wins, so no change is lost.
let pending = 0
function sync() {
    clearTimeout(pending)
    pending = window.setTimeout(rebuild, 50)
}

// What the platform will do, asked of the platform itself. Only it can grant
// permission and only its own settings can take that back, so nothing here is
// remembered — the settings sheet asks again every time it opens.
async function permitted(): Promise<boolean> {
    if (isNative()) return (await plugin()?.checkPermissions())?.display === 'granted'
    return typeof Notification !== 'undefined' && Notification.permission === 'granted'
}

// Asks for permission, and answers what the platform decided. A refusal already
// given comes straight back with no prompt shown at all — Android stops asking
// after two, iOS and the browser after one — which is why the sheet then points
// at the device's own settings.
async function request(): Promise<boolean> {
    if (isNative()) return (await plugin()?.requestPermissions())?.display === 'granted'
    if (typeof Notification === 'undefined') return false
    if (Notification.permission === 'default') await Notification.requestPermission()
    return Notification.permission === 'granted'
}

export default {
    init() {
        if (isNative()) {
            // Tapping a notification opens the day it is about. Registered
            // before the first sync so a cold start from a tap is not missed.
            plugin()?.addListener?.('localNotificationActionPerformed', (e: any) => {
                const d = e?.notification?.extra?.d
                if (d) router.goto(`/day?d=${d}`)
            })
        } else {
            catchUp(new Date())
        }

        sync()
        // Anything that can move a reminder: the occasions and tasks
        // themselves, and a moon-sighting correction, which shifts every hijri
        // anniversary by a day.
        state.on('custom-occasions', sync)
        state.on('tasks', sync)
        state.on('events', sync)
        state.on('hijri', sync)
        // And the day turning over, which spends part of the schedule without
        // changing anything that would announce itself above. The calendar
        // broadcasts this from its midnight timer and when the app is looked at
        // again after hours away (see services/calendar.ts), so this one line
        // covers a tab left open, a tab returned to, and a phone waking up.
        state.on('today', sync)

        // The switch in the settings sheet, watched by value rather than by
        // event: every other setting broadcasts on this channel too, and a
        // theme toggle has no business rebuilding the schedule.
        let off = settings.get().remindersOff
        state.on('settings', () => {
            if (settings.get().remindersOff === off) return
            off = settings.get().remindersOff
            sync()
        })
    },
    allowed: permitted,
    // The settings switch, turned back on. The schedule is rebuilt either way:
    // a permission just granted has reminders waiting to be registered.
    async enable() {
        const ok = await request()
        sync()
        return ok
    },
    // The browser will only grant this from a real gesture, so it is the day
    // page's to call when a reminder is first chosen. The phone is asked by the
    // schedule itself, which is why this leaves it alone.
    async askWebPermission() {
        if (isNative()) return
        await request()
        sync()
    },
}
